// The difficulty curve, FFXI style. Up to level 10 a creature your level is a fair fight alone: that's where you
// learn your job. Past 10, creatures get much tougher with every level and some come in packs, so an even match
// wants a party, and a party is also the fastest way to level (everyone earns more together).
//
// "Checking" a creature compares its level with yours: Too Weak, Easy Prey, Decent Challenge, Even Match, Tough,
// Very Tough, Incredibly Tough. It decides how much experience it's worth, and its name is drawn in that colour.
import type { MobDef } from './mobs.js';

/** Up to this level, an even match is a fair fight alone. */
export const SOLO_LEVEL = 10;

/** How much tougher a creature is past the solo levels (hand-tuned bosses and one-off foes are left alone). */
export function toughness(d: Pick<MobDef, 'level' | 'window' | 'trial' | 'encounter'>): { hp: number; dmg: number } {
  if (d.window || d.trial || d.encounter || d.level <= SOLO_LEVEL) return { hp: 1, dmg: 1 };
  const over = d.level - SOLO_LEVEL;
  return { hp: 1 + 0.22 * over, dmg: 1 + 0.07 * over };
}
export const mobMaxHp = (d: MobDef) => Math.round(d.hp * toughness(d).hp);
export const mobDmg = (d: MobDef): [number, number] => { const k = toughness(d).dmg; return [Math.round(d.dmg[0] * k), Math.round(d.dmg[1] * k)]; };

/** Creatures that come to each other's aid: when one is in a fight, others of its kind this close (tiles) join in. */
export const LINK_TILES = 5;

export type Con = 'weak' | 'easy' | 'decent' | 'even' | 'tough' | 'vtough' | 'itough';
export interface ConInfo { id: Con; name: string; color: string; xp: number }
export const CONS: Record<Con, ConInfo> = {
  weak: { id: 'weak', name: 'Too Weak', color: '#9a948a', xp: 0 },
  easy: { id: 'easy', name: 'Easy Prey', color: '#5b8fd1', xp: 0.4 },
  decent: { id: 'decent', name: 'Decent Challenge', color: '#4f9a5a', xp: 0.75 },
  even: { id: 'even', name: 'Even Match', color: '#3e2f28', xp: 1 },
  tough: { id: 'tough', name: 'Tough', color: '#c99a2e', xp: 1.2 },
  vtough: { id: 'vtough', name: 'Very Tough', color: '#d9703a', xp: 1.4 },
  itough: { id: 'itough', name: 'Incredibly Tough', color: '#c9443a', xp: 1.6 },
};

/** How a creature of `mob` level checks for someone of `you` level. The low end widens as you level, like FFXI. */
export function conOf(mob: number, you: number): ConInfo {
  const d = mob - you, weakAt = you < 10 ? -5 : you < 20 ? -7 : -9;
  if (d <= weakAt) return CONS.weak;
  if (d <= -3) return CONS.easy;
  if (d <= -1) return CONS.decent;
  if (d === 0) return CONS.even;
  if (d <= 2) return CONS.tough;
  if (d <= 4) return CONS.vtough;
  return CONS.itough;
}

/** Everyone in a party earns this much more than they would alone (2: +20%, up to 6: +100%). */
export const partyBonus = (members: number) => 1 + 0.2 * Math.max(0, Math.min(6, members) - 1);

/** Experience for beating a creature, for someone fighting as part of a group whose highest level is `top`
 *  (so a high-level friend can't drag you through creatures that are easy for them). */
export function xpFor(d: MobDef, top: number, members: number): number {
  const con = conOf(d.level, top);
  return Math.round(d.xp * con.xp * partyBonus(members));
}
